import { useState, useCallback, useRef } from 'react';
import {
  Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
  Save, X, Star, StarOff, GripVertical, Upload, Image as ImageIcon
} from 'lucide-react';
import { useAdminProjects } from '../../hooks/useProjects';
import { useEditor } from '../../contexts/EditorContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'mobilität', label: 'Mobilität' },
  { value: 'architektur', label: 'Architektur' },
  { value: 'kommunikation', label: 'Kommunikation' },
  { value: 'event', label: 'Event' },
  { value: 'design', label: 'Design' },
];

function slugify(str) {
  return str.toLowerCase().replace(/[äö]/g, m => m === 'ä' ? 'ae' : 'oe')
    .replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function ProjectManager() {
  const { token } = useEditor();
  const { projects, loading, createProject, updateProject, deleteProject, reorderProjects } = useAdminProjects(token);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startNew = useCallback(() => {
    setEditingId('__new__');
    setFormData({
      slug: '', title: '', category: 'mobilität', shortDescription: '', fullDescription: '',
      coverImage: { url: '', alt: '' }, galleryImages: [],
      tags: [], client: '', scope: '', location: '', year: new Date().getFullYear().toString(),
      challenge: '', solution: '', implementation: '', result: '',
      ctaLabel: 'Projekt anfragen', ctaLink: '/kontakt',
      isVisible: true, featured: false, sortOrder: projects.length,
    });
    setError(null);
  }, [projects.length]);

  const startEdit = useCallback((p) => {
    setEditingId(p.id);
    setFormData({ ...p });
    setError(null);
  }, []);

  const cancel = useCallback(() => { setEditingId(null); setFormData(null); setError(null); }, []);

  const handleSave = useCallback(async () => {
    if (!formData.title || !formData.slug) {
      setError('Titel und Slug sind Pflichtfelder');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId === '__new__') {
        await createProject(formData);
      } else {
        await updateProject(editingId, formData);
      }
      setEditingId(null);
      setFormData(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [editingId, formData, createProject, updateProject]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Projekt wirklich löschen?')) return;
    try { await deleteProject(id); } catch (err) { setError(err.message); }
  }, [deleteProject]);

  const toggleVisible = useCallback(async (p) => {
    await updateProject(p.id, { isVisible: !p.isVisible });
  }, [updateProject]);

  const toggleFeatured = useCallback(async (p) => {
    await updateProject(p.id, { featured: !p.featured });
  }, [updateProject]);

  const moveUp = useCallback(async (index) => {
    if (index <= 0) return;
    const order = projects.map((p, i) => ({ id: p.id, sortOrder: p.sortOrder }));
    const tmp = order[index].sortOrder;
    order[index].sortOrder = order[index - 1].sortOrder;
    order[index - 1].sortOrder = tmp;
    await reorderProjects(order);
  }, [projects, reorderProjects]);

  const moveDown = useCallback(async (index) => {
    if (index >= projects.length - 1) return;
    const order = projects.map((p, i) => ({ id: p.id, sortOrder: p.sortOrder }));
    const tmp = order[index].sortOrder;
    order[index].sortOrder = order[index + 1].sortOrder;
    order[index + 1].sortOrder = tmp;
    await reorderProjects(order);
  }, [projects, reorderProjects]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  if (loading) return <div className="p-4 text-sm text-gray-500">Projekte laden...</div>;

  // Editing form
  if (formData) {
    return (
      <ProjectForm
        formData={formData}
        updateField={updateField}
        setFormData={setFormData}
        onSave={handleSave}
        onCancel={cancel}
        saving={saving}
        error={error}
        isNew={editingId === '__new__'}
        token={token}
      />
    );
  }

  // Project list
  return (
    <div className="h-full flex flex-col" data-testid="project-manager">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Projekte ({projects.length})</span>
        <button onClick={startNew} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-[#1A1A1A] text-white rounded-lg hover:bg-black/80 transition-colors" data-testid="add-project-btn">
          <Plus className="w-3.5 h-3.5" /> Neu
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {projects.map((p, i) => (
          <div key={p.id} className={`flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${!p.isVisible ? 'opacity-40' : ''}`} data-testid={`project-row-${p.id}`}>
            <GripVertical className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
            {p.coverImage?.url ? (
              <img src={p.coverImage.url} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded bg-gray-100 flex items-center justify-center flex-shrink-0"><ImageIcon className="w-4 h-4 text-gray-300" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">{p.title}</p>
              <p className="text-[10px] text-gray-400 truncate">{p.category} · #{p.sortOrder}</p>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button onClick={() => toggleFeatured(p)} className="p-1 rounded hover:bg-gray-100" title={p.featured ? 'Nicht hervorheben' : 'Hervorheben'}>
                {p.featured ? <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> : <StarOff className="w-3.5 h-3.5 text-gray-300" />}
              </button>
              <button onClick={() => toggleVisible(p)} className="p-1 rounded hover:bg-gray-100" title={p.isVisible ? 'Verstecken' : 'Anzeigen'}>
                {p.isVisible ? <Eye className="w-3.5 h-3.5 text-gray-400" /> : <EyeOff className="w-3.5 h-3.5 text-gray-300" />}
              </button>
              <button onClick={() => moveUp(i)} className="p-1 rounded hover:bg-gray-100" disabled={i === 0}><ChevronUp className="w-3.5 h-3.5 text-gray-400" /></button>
              <button onClick={() => moveDown(i)} className="p-1 rounded hover:bg-gray-100" disabled={i === projects.length - 1}><ChevronDown className="w-3.5 h-3.5 text-gray-400" /></button>
              <button onClick={() => startEdit(p)} className="p-1 rounded hover:bg-gray-100 text-gray-500 text-[10px] font-medium">Edit</button>
              <button onClick={() => handleDelete(p.id)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Form ──

function ProjectForm({ formData, updateField, setFormData, onSave, onCancel, saving, error, isNew, token }) {
  return (
    <div className="h-full flex flex-col" data-testid="project-form">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <span className="text-xs font-semibold text-gray-700">{isNew ? 'Neues Projekt' : 'Projekt bearbeiten'}</span>
        <div className="flex items-center gap-1.5">
          <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
          <button onClick={onSave} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-[#1A1A1A] text-white rounded-lg hover:bg-black/80 disabled:opacity-50" data-testid="project-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? '...' : 'Speichern'}
          </button>
        </div>
      </div>
      {error && <div className="mx-3 mt-2 p-2 text-xs text-red-600 bg-red-50 rounded">{error}</div>}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <Field label="Titel *" value={formData.title} onChange={v => { updateField('title', v); if (isNew) updateField('slug', slugify(v)); }} />
        <Field label="Slug *" value={formData.slug} onChange={v => updateField('slug', v)} />
        <SelectField label="Kategorie" value={formData.category} options={CATEGORIES} onChange={v => updateField('category', v)} />

        <div className="flex gap-2">
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={formData.isVisible} onChange={e => updateField('isVisible', e.target.checked)} className="rounded" /> Sichtbar
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={formData.featured} onChange={e => updateField('featured', e.target.checked)} className="rounded" /> Featured
          </label>
        </div>

        <hr className="border-gray-100" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Inhalt</p>
        <Field label="Kurzbeschreibung" value={formData.shortDescription} onChange={v => updateField('shortDescription', v)} />
        <TextareaField label="Vollbeschreibung" value={formData.fullDescription} onChange={v => updateField('fullDescription', v)} />

        <hr className="border-gray-100" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Cover-Bild</p>
        <ImageUploader
          image={formData.coverImage}
          onChange={img => updateField('coverImage', img)}
          token={token}
        />

        <hr className="border-gray-100" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Galerie</p>
        <GalleryManager
          images={formData.galleryImages || []}
          onChange={imgs => updateField('galleryImages', imgs)}
          token={token}
        />

        <hr className="border-gray-100" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Case Study Details</p>
        <Field label="Kunde" value={formData.client} onChange={v => updateField('client', v)} />
        <Field label="Leistungsumfang" value={formData.scope} onChange={v => updateField('scope', v)} />
        <Field label="Standort" value={formData.location} onChange={v => updateField('location', v)} />
        <Field label="Jahr" value={formData.year} onChange={v => updateField('year', v)} />
        <TextareaField label="Herausforderung" value={formData.challenge} onChange={v => updateField('challenge', v)} />
        <TextareaField label="Lösung" value={formData.solution} onChange={v => updateField('solution', v)} />
        <TextareaField label="Umsetzung" value={formData.implementation} onChange={v => updateField('implementation', v)} />
        <TextareaField label="Ergebnis" value={formData.result} onChange={v => updateField('result', v)} />

        <hr className="border-gray-100" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">CTA</p>
        <Field label="CTA Label" value={formData.ctaLabel} onChange={v => updateField('ctaLabel', v)} />
        <Field label="CTA Link" value={formData.ctaLink} onChange={v => updateField('ctaLink', v)} />
        <Field label="Sortierung" value={String(formData.sortOrder)} onChange={v => updateField('sortOrder', parseInt(v) || 0)} />
      </div>
    </div>
  );
}

// ── Reusable fields ──

function Field({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-gray-500 mb-1 block">{label}</span>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-300 focus:border-gray-300 outline-none" />
    </label>
  );
}

function TextareaField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-gray-500 mb-1 block">{label}</span>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3}
        className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-300 focus:border-gray-300 outline-none resize-y" />
    </label>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-gray-500 mb-1 block">{label}</span>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-300 outline-none">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

// ── Image Uploader ──

function ImageUploader({ image, onChange, token }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Upload fehlgeschlagen');
      const data = await res.json();
      onChange({ url: data.url, alt: image?.alt || '' });
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [token, image, onChange]);

  return (
    <div className="space-y-2">
      {image?.url && <img src={image.url} alt={image.alt || ''} className="w-full h-24 object-cover rounded-lg" />}
      <div className="flex gap-2">
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {uploading ? 'Lädt...' : 'Hochladen'}
        </button>
        {image?.url && <button onClick={() => onChange({ url: '', alt: '' })} className="px-2 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg">Entfernen</button>}
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      <Field label="Alt-Text" value={image?.alt || ''} onChange={v => onChange({ ...image, alt: v })} />
    </div>
  );
}

// ── Gallery Manager ──

function GalleryManager({ images, onChange, token }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const addImage = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Upload fehlgeschlagen');
      const data = await res.json();
      onChange([...images, { url: data.url, alt: '' }]);
    } catch (err) {
      console.error('Gallery upload error:', err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [token, images, onChange]);

  const removeImage = useCallback((index) => {
    onChange(images.filter((_, i) => i !== index));
  }, [images, onChange]);

  const updateAlt = useCallback((index, alt) => {
    const updated = [...images];
    updated[index] = { ...updated[index], alt };
    onChange(updated);
  }, [images, onChange]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1.5">
        {images.map((img, i) => (
          <div key={img.url || `gallery-${i}`} className="relative group">
            <img src={img.url} alt={img.alt || ''} className="w-full h-16 object-cover rounded" />
            <button onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-3 h-3" />
            </button>
            <input type="text" value={img.alt || ''} onChange={e => updateAlt(i, e.target.value)} placeholder="Alt..." className="w-full mt-0.5 px-1 py-0.5 text-[10px] border border-gray-200 rounded" />
          </div>
        ))}
      </div>
      <button onClick={() => fileRef.current?.click()} disabled={uploading}
        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 w-full justify-center disabled:opacity-50">
        <Plus className="w-3.5 h-3.5" /> {uploading ? 'Lädt...' : 'Bild hinzufügen'}
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={addImage} />
    </div>
  );
}
